// Shared event-date helpers — single source of truth for Dashboard,
// Events page and EventCalendar.
//
// Rule: an event WITHOUT any usable date is "undated" (TBA). It must NEVER
// be auto-stamped with today/createdAt, and it can never appear on a
// calendar grid cell. Calendars show dated occurrences only; lists show
// dated events plus undated (TBA) ones.

const INACTIVE_SERVICE_STATUS = new Set(['completed', 'cancelled', 'canceled']);

export const isActiveServiceStatus = (status) => {
  const st = String(status || '').trim().toLowerCase();
  return !INACTIVE_SERVICE_STATUS.has(st);
};

// Parse any supported date value into a Date, or null.
// Date-only "YYYY-MM-DD" is interpreted as LOCAL midnight (not UTC midnight)
// so calendar cells, dashboard lists and event filters all agree on the day.
export const parseEventDate = (value) => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const text = String(value).trim();
  if (!text) return null;
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const d = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const isoDatePart = text.match(/^(\d{4})-(\d{2})-(\d{2})[T ]/);
  if (isoDatePart) {
    // ISO datetime without explicit timezone — take the calendar date part as local.
    const d = new Date(
      Number(isoDatePart[1]),
      Number(isoDatePart[2]) - 1,
      Number(isoDatePart[3]),
    );
    if (!Number.isNaN(d.getTime())) {
      const timePart = text.match(/[T ](\d{1,2}):(\d{2})(?::(\d{2}))?/);
      if (timePart) {
        d.setHours(Number(timePart[1]) || 0, Number(timePart[2]) || 0, Number(timePart[3]) || 0, 0);
      }
      return d;
    }
  }
  const d = new Date(text);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const startOfDayLocal = (value) => {
  const d = parseEventDate(value);
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const dayKeyOf = (value) => {
  const d = startOfDayLocal(value);
  return d ? d.getTime() : null;
};

// Active (non completed/cancelled) services that carry a usable date.
export const getActiveDatedServices = (event) => {
  if (!event || !Array.isArray(event.services)) return [];
  return event.services.filter((s) => {
    if (!s || !s.date) return false;
    if (!isActiveServiceStatus(s.status || s.serviceStatus)) return false;
    return parseEventDate(s.date) !== null;
  });
};

// Deduplicated midnight timestamps an event occurs on.
// Falls back to top-level date/startDate ONLY when no active dated service exists.
// Returns [] for undated events. Never falls back to createdAt.
export const getEventOccurrenceKeys = (event) => {
  if (!event) return [];
  const dated = getActiveDatedServices(event);
  const raws = dated.length > 0
    ? dated.map((s) => s.date)
    : event.date
      ? [event.date]
      : event.startDate
        ? [event.startDate]
        : [];
  const seen = new Set();
  const keys = [];
  raws.forEach((raw) => {
    const key = dayKeyOf(raw);
    if (key === null || seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  });
  return keys;
};

export const isUndatedEvent = (event) => getEventOccurrenceKeys(event).length === 0;

// Earliest occurrence (Date at local midnight) or null when undated.
// NOTE: intentionally no createdAt fallback — undated stays undated.
export const getEventAnchorDate = (event) => {
  const keys = getEventOccurrenceKeys(event);
  if (keys.length === 0) return null;
  return new Date(Math.min(...keys));
};

export const eventOccursOnDay = (event, dayKey) => {
  if (dayKey === null || dayKey === undefined) return false;
  return getEventOccurrenceKeys(event).includes(dayKey);
};

export const partitionDatedUndated = (list) => {
  const dated = [];
  const undated = [];
  (Array.isArray(list) ? list : []).forEach((ev) => {
    (isUndatedEvent(ev) ? undated : dated).push(ev);
  });
  return { dated, undated };
};

const eventDates = {
  parseEventDate,
  startOfDayLocal,
  dayKeyOf,
  getActiveDatedServices,
  getEventOccurrenceKeys,
  isUndatedEvent,
  getEventAnchorDate,
  eventOccursOnDay,
  partitionDatedUndated,
};

export default eventDates;
